// src/pages/Optometry/OptometryActivePatientCard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import type { OutletCtx } from "../layout/AppLayout";
import api from "../../../api/axiosConfig";
import "../../../center admin css/LabReportsEntry.css";
import "../../../center admin css/centerAdmin.css";

/** ====== API types (minimal) ====== */
type WaitingItem = {
  _id: string;
  createdAt: string;
  sessionStartAt?: string;
  startedAt?: string; // present when item is inProgress
  stationId: string;
  sessionId?: string;
  session_id?: string;
  session?: { id?: string; sessionId?: string };
  child: {
    name: string;
    age: number;
    gender: string;
    emrId: string;
    childId: string;
    profile?: string;
    photoKey?: string;
    profilePhoto?: string;
  };
};

type StationPatientsResp = {
  waiting?: WaitingItem[];
  inProgress?: WaitingItem[];
  completed?: WaitingItem[];
};

interface ActivePatientCardProps {
  onPatientSelect?: (patient: {
    id: string;
    name: string;
    age: number;
    gender: string;
    emrId?: string;
  }) => void;
  showActions?: boolean; // show Start/Complete buttons
  sessionId?: string; // optional; we still resolve dynamically
  stationId?: string; // default OPTOMETRY
  validateBeforeComplete?: () => boolean;
  onStationStarted?: () => void;
}

/** Pull a session id from an item (supports multiple shapes) */
function readSessionIdFromItem(it?: WaitingItem): string | undefined {
  if (!it) return undefined;
  return (
    it.sessionId ||
    (it as any).session_id ||
    it.session?.id ||
    it.session?.sessionId ||
    undefined
  );
}

/** Fallback to sessionStorage if item doesn’t carry one */
function readSessionIdFromStorage(): string | undefined {
  return (
    sessionStorage.getItem("workflowSessionId") ||
    sessionStorage.getItem("lastSessionId") ||
    sessionStorage.getItem("currentSessionId") ||
    undefined
  );
}

/** Clear old ids after terminal completion */
function clearStoredSessionIds() {
  ["workflowSessionId", "lastSessionId", "currentSessionId"].forEach((k) =>
    sessionStorage.removeItem(k),
  );
}

type Queues = {
  waiting: WaitingItem[];
  inProgress: WaitingItem[];
  completed: WaitingItem[];
};

const OptometryActivePatientCard: React.FC<ActivePatientCardProps> = ({
  onPatientSelect,
  showActions = false,
  sessionId, // no hardcoded default
  stationId = "OPTOMETRY",
  validateBeforeComplete,
  onStationStarted,
}) => {
  // const { activePatient } = useOutletContext<OutletCtx>();
  const outlet = useOutletContext<any>();

  const { activePatient, setActivePatient } = outlet;

  const [loading, setLoading] = useState(false);
  // ✅ NEW: State for avatar
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();

  const [patients, setPatients] = useState<Queues>({
    waiting: [],
    inProgress: [],
    completed: [],
  });
  const [isVisionFormCompleted, setIsVisionFormCompleted] = useState(false);
  const [effectiveSessionId, setEffectiveSessionId] = useState<string>();

  /** Load full queue for this station (with access token) */
  const loadQueue = async () => {
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found. Please log in.");
      const headers = { Authorization: `Bearer ${token}` };

      const centerId =
        sessionStorage.getItem("centerId") || localStorage.getItem("centerId");

      if (!centerId) {
        console.warn("Center ID not found");
        setPatients({ waiting: [], inProgress: [], completed: [] });
        return;
      }

      const date = new Date().toISOString().slice(0, 10);

      const { data } = await api.centerworkflowApi.get(
        `/queues/OPTOMETRY_Q?center_id=${encodeURIComponent(centerId)}&date=${encodeURIComponent(date)}`,
        { headers },
      );

      setPatients({
        waiting: [],
        inProgress: data?.inProgress ?? [],
        completed: [],
      });
    } catch (e) {
      console.warn(
        "OptometryActivePatientCard: failed to load station queue",
        e,
      );
      setPatients({ waiting: [], inProgress: [], completed: [] });
    }
  };

  useEffect(() => {
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  // const current = activePatient;

  const current = useMemo(() => {
    if (activePatient) return activePatient;

    if (patients.inProgress.length > 0) {
      return patients.inProgress[0];
    }

    return null;
  }, [activePatient, patients.inProgress]);

  /** Resolve session id: prop → current item → sessionStorage */
  useEffect(() => {
    const fromItem = readSessionIdFromItem(current);
    const fromStore = readSessionIdFromStorage();
    const resolved = sessionId || fromItem || fromStore;
    setEffectiveSessionId(resolved);
  }, [sessionId, current]);

  /* ================= IMAGE FETCH LOGIC (FIXED) ================= */
  useEffect(() => {
    const loadAvatar = async () => {
      // 1. Reset logic
      setAvatarUrl(undefined);

      // if (!current?.child?.emrId) return;
      if (!current?.child?.childId) return;

      try {
        const token = sessionStorage.getItem("accessToken");
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };

        const filePath =
          current?.child?.profile ||
          current?.child?.photoKey ||
          current?.child?.profilePhoto ||
          "";

        if (!filePath) {
          console.log(
            "No photo key found in Optometry Center Workflow patient:",
            current,
          );
          return;
        }

        // Optimization: if signed, set immediately
        if (filePath.includes("X-Amz-Signature")) {
          setAvatarUrl(filePath);
          return;
        }

        // ✅ FIX: Manual string splitting instead of new URL()
        // const parts = filePath.split("/");

        // // Safety check
        // if (parts.length < 3) {
        //   console.warn("Invalid file path format:", filePath);
        //   return;
        // }

        // // Extract Module
        // const module = `${parts[0]}/${parts[1]}`;

        // // Extract Key
        // const key = parts.slice(2).join("/");

        const cleanPath = filePath.startsWith("/")
          ? filePath.slice(1)
          : filePath;

        const parts = cleanPath.split("/");

        // Safety check
        if (parts.length < 3) {
          console.warn("Invalid file path format:", filePath);
          return;
        }

        // Extract Module
        const module = `${parts[0]}/${parts[1]}`;

        // Extract Key
        const key = parts.slice(2).join("/");

        const img = await api.S3config.get("/view-url", {
          params: { module, key },
          headers,
        });

        const signedUrl = img.data?.url || (img as any).url;

        if (signedUrl) {
          setAvatarUrl(signedUrl);
        }
      } catch (err) {
        console.error("Optometry avatar load failed", err);
      }
    };

    loadAvatar();
  }, [
    current?.child?.childId,
    current?.child?.profile,
    current?.child?.photoKey,
    current?.child?.profilePhoto,
  ]);
  /* ================================================= */

  /* ================================================= */

  /** Bubble selection up (so pages that listen can react) */
  useEffect(() => {
    if (!current) return;
    onPatientSelect?.({
      id: current._id,
      name: current.child?.name ?? "",
      age: Number(current.child?.age ?? 0),
      gender: String(current.child?.gender || "M")
        .toUpperCase()
        .startsWith("F")
        ? "Female"
        : "Male",
      emrId: current.child?.childId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?._id]);

  const displayAge = current?.child?.age ? `${current.child.age} Years` : "-";

  const displayGender = current?.child?.gender
    ? String(current.child.gender).toUpperCase().startsWith("F")
      ? "Female"
      : "Male"
    : "-";

  /** Start / Complete actions (use resolved session id) */
  // const doPost = async (path: "start" | "complete") => {
  const doPost = async (path: "start" | "complete" | "cancel") => {
    // if (!effectiveSessionId || !stationId) {
    const sessionId = effectiveSessionId || sessionStorage.getItem("sessionId");

    if (!sessionId || !stationId) {
      alert(
        "No active session id found. Pick a patient or start a new session.",
      );
      return;
    }
    try {
      setLoading(true);
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found.");
      const headers = { Authorization: `Bearer ${token}` };

      let body: any = {};

      if (path === "complete") {
        body = {
          completedForms: ["VISION_SCREENING"],
          payload: {},
        };
      }

      const res = await api.centerworkflowApi.post(
        `/sessions/${encodeURIComponent(
          sessionId,
        )}/stations/${encodeURIComponent(stationId)}/${path}`,
        body,
        { headers },
      );

      if (path === "start") {
        alert(`Station "${stationId}" started successfully`);
        onStationStarted?.();
        window.dispatchEvent(new Event("patientUpdated"));
      } else if (path === "cancel") {
        alert(`Station "${stationId}" cancelled successfully`);
      } else {
        if (res.data?.isComplete) {
          clearStoredSessionIds();

          setActivePatient?.(null);

          sessionStorage.removeItem("selectedPatient_OPTOMETRY_Q");

          alert("🎉 Workflow completed for this session");
        } else if (res.data?.nextStation) {
          clearStoredSessionIds();

          setActivePatient?.(null);

          sessionStorage.removeItem("selectedPatient_OPTOMETRY_Q");

          alert(`Next station: ${res.data.nextStation}`);
        } else {
          clearStoredSessionIds();

          setActivePatient?.(null);

          sessionStorage.removeItem("selectedPatient_OPTOMETRY_Q");

          alert("Station completed");
        }

        window.dispatchEvent(new Event("patientUpdated"));
      }

      // Refresh full queue so the card keeps showing in-progress child
      // await loadQueue();
      if (path !== "start") {
        await loadQueue();
      }

      window.dispatchEvent(new Event("patientUpdated"));
    } catch (err) {
      console.error(`❌ Error on ${path}:`, err);
      alert(`Failed to ${path} station`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="patient-details-card" style={{ marginBottom: 18 }}>
      <div className="patient-details-top">
        {/* ✅ NEW: Display Avatar */}
        <div className="patient-avatar-large" style={{ overflow: "hidden" }}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Patient"
              className="patient-avatar-img"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div className="patient-avatar-fallback" />
          )}
        </div>

        <div className="patient-info-group">
          <div className="patient-info-row">
            <div>
              <span className="field-label">EMR ID</span>
              <div className="field-value">
                {current?.child?.childId ?? "-"}
              </div>
            </div>
            <div>
              <span className="field-label">Name</span>
              <div className="field-value">{current?.child?.name ?? "-"}</div>
            </div>
          </div>

          <div className="patient-info-row">
            <div>
              <span className="field-label">Age</span>
              <div className="field-value">{displayAge}</div>
            </div>
            <div>
              <span className="field-label">Gender</span>
              <div className="field-value">{displayGender}</div>
            </div>
          </div>
        </div>
      </div>

      {showActions && (
        <div
          className="patient-action-buttons"
          style={{ marginTop: 16, display: "flex", gap: "12px" }}
        >
          <button
            className="start-btn"
            style={{
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => current && doPost("start")}
            disabled={loading || !current}
          >
            {loading ? "Starting..." : "Start"}
          </button>

          <button
            className="cancel-button"
            style={{
              backgroundColor: "#FF9800", // 🟠 ORANGE
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => doPost("cancel")}
            disabled={loading}
          >
            {loading ? "Cancelling..." : "Cancel"}
          </button>

          <button
            className="end-btn"
            style={{
              backgroundColor: "#f44336",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => {
              if (validateBeforeComplete && !validateBeforeComplete()) return;
              doPost("complete");
            }}
            disabled={loading}
          >
            {loading ? "Completing..." : "Complete"}
          </button>
        </div>
      )}
    </div>
  );
};

export default OptometryActivePatientCard;
